import { memo, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { getActiveUser } from "services/api/users";
import { getContactEmailsAPI } from "services/api/contacts/api";
import { ContactEmailRow } from "services/api/contacts/types";
import { Errors } from "services/forms/validation/types";
import { validateYup } from "services/forms/validation/validation";
import { initialPagination } from "ui/components/Table/consts";
import { FileExtended } from "ui/components/FileUpload/FileDropZone/types";

import Modal from "ui/components/Modal/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalFooter from "ui/components/Modal/ModalFooter";
import { Button } from "ui/components/Button/Button";
import Loader from "ui/components/Loader/Loader";
import { ZPosition } from "ui/components/Modal/types";

import { initialEmailForm, SENDER_EMAIL } from "ui/components/Modal/EmailSendModal/consts";

import { EMAIL_SEND_MODAL_VALIDATION_SCHEMA } from "./validation";
import { EmailFormValues, EmailSendModalProps } from "./types";
import EmailContentSection from "./EmailContent";
import EmailRecipientsSection from "./EmailRecipients";
import EmailAttachmentsSection from "./EmailAttachments";


const EmailSendModal: React.FC<EmailSendModalProps> = (props) => {
  const { show, onHide, title, initialFormValues, contactId, onSend, isLoading: externalLoading } = props;

  const { user } = useSelector(getActiveUser);

  const tenantEmail = user?.tenant?.email ?? "";
  const companyName = user?.tenant?.companyName ?? "";

  const [formValues, setFormValues] = useState<EmailFormValues>(initialEmailForm);
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!show) return;

    setFormValues({
      ...initialEmailForm,
      replyTo: tenantEmail,
      ...initialFormValues,
    });
    setErrors({});

    if (contactId) {
      fetchContactEmails(contactId);
    }
  }, [show]);

  useEffect(() => {
    if (initialFormValues?.pdfAttachment) {
      setFormValues((prev) => ({
        ...prev,
        pdfAttachment: initialFormValues.pdfAttachment ?? null,
      }));
    }
  }, [initialFormValues?.pdfAttachment]);

  const fetchContactEmails = async (cId: string) => {
    setIsLoading(true);
    try {
      const result = await getContactEmailsAPI(
        { ...initialPagination, pageSize: 100 },
        cId
      );
      if (result?.data) {
        setFormValues((prev) => ({
          ...prev,
          contactEmails: result.data.map((email: ContactEmailRow) => ({
            emailAddress: email.emailAddress,
            description: email.description,
            selected: email.primary,
          })),
        }));
      }
    } catch {
      // ignore
    }
    setIsLoading(false);
  };

  const handleAddEmail = useCallback(() => {
    const trimmed = formValues.newEmail.trim().toLowerCase();

    const isValid = validateYup(
      { newEmail: trimmed },
      EMAIL_SEND_MODAL_VALIDATION_SCHEMA(formValues.additionalEmails, formValues.contactEmails),
      setErrors
    );

    if (!isValid) return;
    setFormValues((prev) => ({
      ...prev,
      additionalEmails: [...prev.additionalEmails, { emailAddress: trimmed, selected: true }],
      newEmail: "",
    }));
  }, [formValues.newEmail, formValues.additionalEmails, formValues.contactEmails]);

  const handleUploadSave = useCallback((uploads: any[]) => {
    const newItems = uploads.map((upload) => ({
      file: {
        id: upload.id,
        name: upload.originalFileName,
        url: upload.url,
        title: upload.originalFileName,
        isNew: false,
        size: upload.fileSizeKb * 1024,
        type: upload.contentType,
        lastModified: Date.now(),
        arrayBuffer: () => Promise.resolve(new ArrayBuffer(0)),
        slice: () => new Blob(),
        stream: () => new ReadableStream(),
        text: () => Promise.resolve(""),
        webkitRelativePath: "",
      } as unknown as FileExtended,
      selected: true,
    }));
    setFormValues((prev) => ({
      ...prev,
      additionalFiles: [...prev.additionalFiles, ...newItems],
    }));
  }, []);

  const handleSend = async () => {
    const selectedEmails = formValues.contactEmails
      .filter((e) => e.selected)
      .map((e) => e.emailAddress);

    const selectedAdditional = formValues.additionalEmails
      .filter((e) => e.selected)
      .map((e) => e.emailAddress);

    const allRecipients = [...selectedEmails, ...selectedAdditional];

    if (allRecipients.length === 0) {
      setErrors({ recipients: "Molimo odaberite barem jednog primatelja" });
      return;
    }

    const attachments: FileExtended[] = [
      ...(formValues.pdfAttachment && formValues.pdfAttachmentSelected ? [formValues.pdfAttachment] : []),
      ...formValues.additionalFiles.filter((item) => item.selected).map((item) => item.file),
    ];

    setIsLoading(true);
    try {
      await onSend({
        recipients: allRecipients,
        cc: formValues.ccTenantEmail ? formValues.replyTo : null,
        replyTo: formValues.replyTo,
        subject: formValues.subject,
        content: formValues.content,
        attachments,
      });
      onHide();
    } catch {
      // ignore
    }
    setIsLoading(false);
  };

  const noteText = `Napomena: Ova je poruka poslana u ime autora poruke ${companyName} s adrese poslužitelja ${SENDER_EMAIL}, stoga nemojte odgovarati na nju. Autoru poruke možete odgovoriti na ${formValues.replyTo}. Ako ne želite primati takve poruke ili smatrate da vam je poruka poslana greškom, obratite se autoru poruke.`;

  if (isLoading || externalLoading) {
    return (
      <Modal show={show} position="centered" size="large" onHide={onHide}>
        <Loader isLoading={true}>{null}</Loader>
      </Modal>
    );
  }

  return (
    <Modal
      show={show}
      position="centered"
      size="large"
      onHide={onHide}
    
      zPosition={ZPosition.Top}
    >
      <ModalHeader
          icon="/media/images/add-icon.svg"
          title={title}
          onHide={onHide}
        />
      <ModalBody>
        <div className="flex gap-6 p-4">

          {/* Left Column - Email Form */}
          <EmailContentSection
            formValues={formValues}
            setFormValues={setFormValues}
            noteText={noteText}
            senderEmail={SENDER_EMAIL}
            tenantEmail={tenantEmail}
          />

          {/* Right Column */}
          <div className="flex flex-col w-1/3">
            <EmailRecipientsSection
              formValues={formValues}
              setFormValues={setFormValues}
              onAddEmail={handleAddEmail}
              errors={errors}
              onClearErrors={() => setErrors({})}
            />

            <EmailAttachmentsSection
              formValues={formValues}
              setFormValues={setFormValues}
              onUploadSave={handleUploadSave}
            />
          </div>
        </div>
      </ModalBody>

      <ModalFooter>
        <div className="px-3 py-3 fixed left-0 bottom-0 bg-slate-50 border border-t-slate-200 w-full flex justify-end gap-3">
          <Button variant="outlined" onClick={onHide}>
            Odustani
          </Button>
          <Button variant="primary" onClick={handleSend}>
            Pošaljite
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default memo(EmailSendModal);
